import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, Edit2Icon, PlusIcon, BookOpenIcon, PlayIcon, TargetIcon, DollarSignIcon, UsersIcon, TrendingUpIcon, DatabaseIcon, ClockIcon } from 'lucide-react';
export function ProgramDetailPage() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'summary' | 'inputs' | 'performance' | 'dependencies' | 'audit'>('summary');
  // Mock program data
  const program = {
    id: 1,
    name: 'In-Market Account Acceleration',
    objective: 'Pipeline Generation',
    timeframe: '90 days',
    owner: 'Sarah Chen',
    status: 'live',
    targetAccounts: 500,
    budget: 450000,
    headcount: 12,
    targetPipeline: 3500000,
    actualPipeline: 2800000,
    meetings: 234,
    opportunities: 45,
    expansionARR: 0,
    avgEngagementTime: '4.2 days'
  };
  const playbooks = [{
    id: 1,
    name: 'Enterprise Discovery',
    stage: 'Awareness',
    plays: 4,
    status: 'active'
  }, {
    id: 2,
    name: 'Executive Engagement',
    stage: 'Consideration',
    plays: 5,
    status: 'active'
  }];
  const plays = [{
    id: 1,
    name: 'High-Intent Account Blitz',
    playbook: 'Enterprise Discovery',
    accounts: 89,
    status: 'running'
  }, {
    id: 2,
    name: 'Executive Nurture',
    playbook: 'Executive Engagement',
    accounts: 67,
    status: 'running'
  }];
  const orchestrations = [{
    id: 1,
    name: 'Multi-Channel Outreach',
    type: 'Sequence',
    status: 'active'
  }, {
    id: 2,
    name: 'Intent Signal Response',
    type: 'Trigger',
    status: 'active'
  }];
  const agents = ['Research Agent', 'Personalization Agent', 'Outreach Agent'];
  const dependencies = [{
    type: 'Signal',
    name: 'Website Visit',
    status: 'healthy'
  }, {
    type: 'Integration',
    name: 'Salesforce',
    status: 'healthy'
  }, {
    type: 'Data',
    name: 'Contact Data',
    freshness: '< 24h'
  }];
  const tabs = [{
    id: 'summary',
    label: 'Summary'
  }, {
    id: 'inputs',
    label: 'Plan Inputs'
  }, {
    id: 'performance',
    label: 'Performance'
  }, {
    id: 'dependencies',
    label: 'Dependencies'
  }, {
    id: 'audit',
    label: 'Audit'
  }];
  return <div className="w-full min-h-screen bg-gray-50" data-id="element-6144">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4" data-id="element-6145">
        <div className="flex items-center gap-4 mb-4" data-id="element-6146">
          <button onClick={() => navigate('/programs')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-6147">
            <ArrowLeftIcon className="w-5 h-5 text-gray-600" data-id="element-6148" />
          </button>
          <div className="flex-1" data-id="element-6149">
            <div className="flex items-center gap-3" data-id="element-6150">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-6151">
                {program.name}
              </h1>
              <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full" data-id="element-6152">
                {program.status}
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-1" data-id="element-6153">
              {program.timeframe} • Owner: {program.owner} • Objective:{' '}
              {program.objective}
            </p>
          </div>
          <div className="flex items-center gap-3" data-id="element-6154">
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-6155">
              <PlusIcon className="w-4 h-4" data-id="element-6156" />
              Add Playbook
            </button>
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-6157">
              <PlusIcon className="w-4 h-4" data-id="element-6158" />
              Add Play
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors" data-id="element-6159">
              <Edit2Icon className="w-4 h-4" data-id="element-6160" />
              Edit Program
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="flex gap-6" data-id="element-6161">
          {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as any)} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? 'border-red-600 text-red-600' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-6162">
              {tab.label}
            </button>)}
        </div>
      </div>
      {/* Content */}
      <div className="p-6" data-id="element-6163">
        {activeTab === 'summary' && <div className="space-y-6" data-id="element-6164">
            {/* KPIs */}
            <div className="grid grid-cols-4 gap-6" data-id="element-6165">
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-6166">
                <div className="flex items-center justify-between mb-2" data-id="element-6167">
                  <p className="text-sm text-gray-600" data-id="element-6168">Pipeline Created</p>
                  <DollarSignIcon className="w-5 h-5 text-green-600" data-id="element-6169" />
                </div>
                <p className="text-3xl font-bold text-gray-900" data-id="element-6170">
                  ${(program.actualPipeline / 1000000).toFixed(1)}M
                </p>
                <div className="mt-2" data-id="element-6171">
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-6172">
                    <div className="h-full bg-green-600 rounded-full" style={{
                  width: `${program.actualPipeline / program.targetPipeline * 100}%`
                }} data-id="element-6173" />
                  </div>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-6174">
                    {Math.round(program.actualPipeline / program.targetPipeline * 100)}
                    % of ${(program.targetPipeline / 1000000).toFixed(1)}M
                    target
                  </p>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-6175">
                <div className="flex items-center justify-between mb-2" data-id="element-6176">
                  <p className="text-sm text-gray-600" data-id="element-6177">Meetings Created</p>
                  <UsersIcon className="w-5 h-5 text-blue-600" data-id="element-6178" />
                </div>
                <p className="text-3xl font-bold text-gray-900" data-id="element-6179">
                  {program.meetings}
                </p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-6180">
                <div className="flex items-center justify-between mb-2" data-id="element-6181">
                  <p className="text-sm text-gray-600" data-id="element-6182">Opportunities</p>
                  <TargetIcon className="w-5 h-5 text-purple-600" data-id="element-6183" />
                </div>
                <p className="text-3xl font-bold text-gray-900" data-id="element-6184">
                  {program.opportunities}
                </p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-6185">
                <div className="flex items-center justify-between mb-2" data-id="element-6186">
                  <p className="text-sm text-gray-600" data-id="element-6187">Avg. Engagement Time</p>
                  <ClockIcon className="w-5 h-5 text-orange-600" data-id="element-6188" />
                </div>
                <p className="text-3xl font-bold text-gray-900" data-id="element-6189">
                  {program.avgEngagementTime}
                </p>
              </div>
            </div>
            {/* Playbooks */}
            <div className="bg-white rounded-lg border border-gray-200" data-id="element-6190">
              <div className="px-6 py-4 border-b border-gray-200" data-id="element-6191">
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-6192">
                  Playbooks Attached ({playbooks.length})
                </h2>
              </div>
              <div className="p-6" data-id="element-6193">
                <div className="space-y-3" data-id="element-6194">
                  {playbooks.map(playbook => <div key={playbook.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200 flex items-center justify-between" data-id="element-6195">
                      <div className="flex items-center gap-3" data-id="element-6196">
                        <BookOpenIcon className="w-5 h-5 text-purple-600" data-id="element-6197" />
                        <div data-id="element-6198">
                          <p className="text-sm font-semibold text-gray-900" data-id="element-6199">
                            {playbook.name}
                          </p>
                          <p className="text-xs text-gray-600" data-id="element-6200">
                            {playbook.stage} • {playbook.plays} plays
                          </p>
                        </div>
                      </div>
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded" data-id="element-6201">
                        {playbook.status}
                      </span>
                    </div>)}
                </div>
              </div>
            </div>
            {/* Plays Running */}
            <div className="bg-white rounded-lg border border-gray-200" data-id="element-6202">
              <div className="px-6 py-4 border-b border-gray-200" data-id="element-6203">
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-6204">
                  Plays Running ({plays.length})
                </h2>
              </div>
              <div className="p-6" data-id="element-6205">
                <div className="space-y-3" data-id="element-6206">
                  {plays.map(play => <div key={play.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200 flex items-center justify-between" data-id="element-6207">
                      <div className="flex items-center gap-3" data-id="element-6208">
                        <PlayIcon className="w-5 h-5 text-blue-600" data-id="element-6209" />
                        <div data-id="element-6210">
                          <p className="text-sm font-semibold text-gray-900" data-id="element-6211">
                            {play.name}
                          </p>
                          <p className="text-xs text-gray-600" data-id="element-6212">
                            {play.playbook} • {play.accounts} accounts
                          </p>
                        </div>
                      </div>
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded" data-id="element-6213">
                        {play.status}
                      </span>
                    </div>)}
                </div>
              </div>
            </div>
            {/* Orchestrations */}
            <div className="bg-white rounded-lg border border-gray-200" data-id="element-6214">
              <div className="px-6 py-4 border-b border-gray-200" data-id="element-6215">
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-6216">
                  Orchestrations Running ({orchestrations.length})
                </h2>
              </div>
              <div className="p-6" data-id="element-6217">
                <div className="space-y-3" data-id="element-6218">
                  {orchestrations.map(orch => <div key={orch.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200 flex items-center justify-between" data-id="element-6219">
                      <div data-id="element-6220">
                        <p className="text-sm font-semibold text-gray-900" data-id="element-6221">
                          {orch.name}
                        </p>
                        <p className="text-xs text-gray-600" data-id="element-6222">{orch.type}</p>
                      </div>
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded" data-id="element-6223">
                        {orch.status}
                      </span>
                    </div>)}
                </div>
              </div>
            </div>
            {/* Agents */}
            <div className="bg-white rounded-lg border border-gray-200" data-id="element-6224">
              <div className="px-6 py-4 border-b border-gray-200" data-id="element-6225">
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-6226">
                  Agents Used ({agents.length})
                </h2>
              </div>
              <div className="p-6" data-id="element-6227">
                <div className="flex flex-wrap gap-2" data-id="element-6228">
                  {agents.map(agent => <span key={agent} className="px-3 py-2 bg-purple-100 text-purple-700 text-sm font-medium rounded-lg" data-id="element-6229">
                      {agent}
                    </span>)}
                </div>
              </div>
            </div>
          </div>}
        {activeTab === 'inputs' && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-6230">
            <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-6231">
              Plan Inputs
            </h2>
            <div className="space-y-6" data-id="element-6232">
              <div data-id="element-6233">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-6234">
                  Target Accounts/Segments
                </label>
                <input type="text" value={program.targetAccounts} className="w-full px-3 py-2 border border-gray-300 rounded-lg" readOnly data-id="element-6235" />
              </div>
              <div data-id="element-6236">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-6237">
                  Budget
                </label>
                <input type="text" value={`$${program.budget.toLocaleString()}`} className="w-full px-3 py-2 border border-gray-300 rounded-lg" readOnly data-id="element-6238" />
              </div>
              <div data-id="element-6239">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-6240">
                  Headcount
                </label>
                <input type="text" value={`${program.headcount} FTEs`} className="w-full px-3 py-2 border border-gray-300 rounded-lg" readOnly data-id="element-6241" />
              </div>
            </div>
          </div>}
        {activeTab === 'performance' && <div className="space-y-6" data-id="element-6242">
            <div className="grid grid-cols-2 gap-6" data-id="element-6243">
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-6244">
                <h3 className="text-sm font-medium text-gray-700 mb-4" data-id="element-6245">
                  Pipeline Created
                </h3>
                <p className="text-3xl font-bold text-gray-900 mb-2" data-id="element-6246">
                  ${(program.actualPipeline / 1000000).toFixed(1)}M
                </p>
                <div className="h-48 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center" data-id="element-6247">
                  <p className="text-gray-500" data-id="element-6248">Chart would appear here</p>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-6249">
                <h3 className="text-sm font-medium text-gray-700 mb-4" data-id="element-6250">
                  Meetings & Opportunities
                </h3>
                <div className="space-y-3 mb-4" data-id="element-6251">
                  <div data-id="element-6252">
                    <p className="text-sm text-gray-600" data-id="element-6253">Meetings</p>
                    <p className="text-2xl font-bold text-gray-900" data-id="element-6254">
                      {program.meetings}
                    </p>
                  </div>
                  <div data-id="element-6255">
                    <p className="text-sm text-gray-600" data-id="element-6256">Opportunities</p>
                    <p className="text-2xl font-bold text-gray-900" data-id="element-6257">
                      {program.opportunities}
                    </p>
                  </div>
                </div>
                <div className="h-24 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center" data-id="element-6258">
                  <p className="text-gray-500" data-id="element-6259">Chart would appear here</p>
                </div>
              </div>
            </div>
          </div>}
        {activeTab === 'dependencies' && <div className="bg-white rounded-lg border border-gray-200" data-id="element-6260">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-6261">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-6262">
                Dependencies
              </h2>
            </div>
            <div className="p-6" data-id="element-6263">
              <div className="space-y-4" data-id="element-6264">
                {dependencies.map((dep, index) => <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200 flex items-center justify-between" data-id="element-6265">
                    <div data-id="element-6266">
                      <p className="text-sm font-semibold text-gray-900" data-id="element-6267">
                        {dep.name}
                      </p>
                      <p className="text-xs text-gray-600" data-id="element-6268">{dep.type}</p>
                      {dep.freshness && <p className="text-xs text-gray-600 mt-1" data-id="element-6269">
                          Freshness: {dep.freshness}
                        </p>}
                    </div>
                    {dep.status && <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded" data-id="element-6270">
                        {dep.status}
                      </span>}
                  </div>)}
              </div>
            </div>
          </div>}
        {activeTab === 'audit' && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-6271">
            <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-6272">
              Audit Trail
            </h2>
            <div className="h-64 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center" data-id="element-6273">
              <p className="text-gray-500" data-id="element-6274">
                Change log and approval history would appear here
              </p>
            </div>
          </div>}
      </div>
    </div>;
}